import React from 'react'
import { Button, Container, Typography } from '@mui/material'
import { BG_COLORS } from './constants'
import { Fade } from 'react-awesome-reveal'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { maybe } from '../../../utils/misc'
import { WholesaleProductQueryResult } from '@graphql/queries/__generated__/wholesale.generated'
import { getLightboxUiElements } from '../../../utils/imagesLightbox'
import { Gallery, Item } from 'react-photoswipe-gallery'
import { UserFragment } from '@graphql/fragments/__generated__/User.generated'
import { WholesaleAppStatusEnum } from '../../../../generated/gqlTypes'
import { Link } from 'react-router-dom'
import { getCategoryUrl, getProductsDataSheetDownloadUrl } from '../../../urls'
import { useTranslation } from 'react-i18next'

interface Props {
  product: WholesaleProductQueryResult['data']['wholesaleProduct'] | null
  user: UserFragment | null
}

const Page: React.FC<Props> = ({ product, user }) => {
  const { t } = useTranslation()
  if (!product) return null

  const swiperOptions: SwiperOptions = {
    spaceBetween: 5,
    slidesPerView: 1,
    lazy: true,
    preloadImages: false,
    watchSlidesProgress: true,
    loop: false,
  }
  const descriptions = maybe(() => product.description, {
    description1: '',
    description2: '',
    description3: '',
    description4: '',
  })
  const items = product.elements.map(node => {
    return {
      id: node.id,
      title: node.name,
      description: node.description,
      images: node.images,
    }
  })
  const isUserWholesale = user?.wholesaleApp?.status === WholesaleAppStatusEnum.Confirmed
  const isStaff = user?.isStaff
  const categoryUrl = product.category ? getCategoryUrl(product.category.slug, product.category.pk) : null
  const resultFile = product.category?.productsDataFile?.resultFile
    ? getProductsDataSheetDownloadUrl(product.category.productsDataFile.pk)
    : null
  return (
    <div>
      <section>
        <Container maxWidth='lg' sx={{ backgroundColor: BG_COLORS.LIGHT_BLUE }}>
          <div className='text-center min-h-200 flex justify-center items-center flex-col py-20'>
            <Typography variant='body1' component='div'>
              <div dangerouslySetInnerHTML={{ __html: descriptions.description2 }} />
            </Typography>
          </div>
        </Container>
      </section>

      <section>
        <Container maxWidth='lg' disableGutters>
          <div className='relative' style={{ paddingBottom: '46%' }}>
            <div className='absolute inset-0'>
              <img className='max-w-full' src={product.rectangularImage} alt={product.name} />
            </div>
          </div>
        </Container>
      </section>

      <section>
        <Container maxWidth='lg' sx={{ backgroundColor: BG_COLORS.GRAY }}>
          <div className='text-center min-h-100 pt-20 md:pt-60 pb-20'>
            <Typography variant='body1' component='div'>
              <div dangerouslySetInnerHTML={{ __html: descriptions.description3 }} />
            </Typography>
          </div>
          <div className='flex items-center justify-center flex-wrap my-10'>
            {(isUserWholesale || isStaff) && resultFile && (
              <span className='mr-0 sm:mr-10 mb-10 w-full sm:w-auto'>
                <Button variant='outlined' size='large' href={resultFile} target='_blank' fullWidth download>
                  <span className='capitalize'>{t('button.downloadProductsData', 'Download Products Data')}</span>
                </Button>
              </span>
            )}
            {categoryUrl && (
              <span className='mb-10 w-full sm:w-auto'>
                <Button variant='outlined' size='large' component={Link} to={categoryUrl} target='_blank' fullWidth>
                  <span className='capitalize'>{t('button.viewAllDesigns', 'View All Designs')}</span>
                </Button>
              </span>
            )}
          </div>
        </Container>
      </section>

      <section>
        <Container maxWidth='lg' sx={{ backgroundColor: BG_COLORS.LIGHT_BLUE }}>
          <div className='text-center min-h-150 flex justify-center items-center flex-col py-20'>
            <Typography variant='body1' component='div'>
              <div dangerouslySetInnerHTML={{ __html: descriptions.description4 }} />
            </Typography>
          </div>
        </Container>
      </section>

      <section className='mt-20 md:mt-60'>
        <Container maxWidth='lg' disableGutters>
          {items.map((item, idx) => {
            const el = `swiper-pagination-images-${idx}`
            const navigation =
              item.images.length > 1
                ? {
                    prevEl: `#swiper-button-prev-images-${idx}`,
                    nextEl: `#swiper-button-next-popular-${idx}`,
                  }
                : false
            const slideNextPrev = {
              allowSlideNext: item.images.length > 1,
              allowSlidePrev: item.images.length > 1,
            }
            return (
              <Gallery key={item.id} uiElements={getLightboxUiElements()} options={{ loop: false }} withDownloadButton>
                <div className='flex flex-col md:flex-row mb-50'>
                  <div className='flex justify-center w-full md:w-450'>
                    <div className='w-full md:w-350 relative'>
                      <Swiper
                        {...swiperOptions}
                        pagination={{ clickable: true, el: `#${el}` }}
                        navigation={navigation}
                        {...slideNextPrev}
                      >
                        {item.images.map((image, idx) => (
                          <SwiperSlide key={image.id}>
                            <Item
                              original={image.image}
                              thumbnail={image.thumbnail}
                              width={image.width}
                              height={image.height}
                              alt={`${item.title}_${idx}`}
                            >
                              {({ ref, open }) => (
                                <img
                                  className='cursor-pointer w-full'
                                  src={image.thumbnail}
                                  ref={ref as React.MutableRefObject<HTMLImageElement>}
                                  onClick={open}
                                  alt={`${item.title}_${idx}`}
                                />
                              )}
                            </Item>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      <div
                        id={el}
                        className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
                          item.images.length > 1 ? 'block' : 'hidden'
                        }`}
                      />
                      {navigation && (
                        <>
                          <div id={navigation.prevEl.slice(1)} className='swiper-button-prev' />
                          <div id={navigation.nextEl.slice(1)} className='swiper-button-next' />
                        </>
                      )}
                    </div>
                  </div>

                  <div className='text-center mt-20 px-10 flex-1'>
                    <Fade triggerOnce direction='right'>
                      <Typography variant='h3' marginBottom={2}>
                        <span className='text-4xl md:text-5xl'>{item.title}</span>
                      </Typography>
                      <Typography variant='body1' component='div'>
                        <div dangerouslySetInnerHTML={{ __html: item.description }} />
                      </Typography>
                    </Fade>
                  </div>
                </div>
              </Gallery>
            )
          })}
        </Container>
      </section>
    </div>
  )
}

export default Page
